import React, { useEffect, useRef, useState } from 'react';
import { Music, Volume1, Volume2, VolumeX, Sparkles } from 'lucide-react';

import { setAudioSettings, useAudioSettings, playSelectSound } from '../lib/sound';

/**
 * Sonido de la partida, a gusto de cada jugador: silenciar todo o regular la
 * música y los efectos por separado. Se guarda en su navegador; nadie más lo
 * nota.
 */
export const AudioControls: React.FC<{
  className?: string;
  /** Muestra el texto junto al icono: en pantallas despejadas un icono suelto no se entiende. */
  labeled?: boolean;
  /** Lado hacia el que se abre el panel; 'right' cuando el botón va en la esquina derecha. */
  align?: 'left' | 'right';
}> = ({ className = '', labeled = false, align = 'left' }) => {
  const s = useAudioSettings();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onDown = (e: PointerEvent) => {
      if (!ref.current?.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && setOpen(false);
    window.addEventListener('pointerdown', onDown);
    window.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('pointerdown', onDown);
      window.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const silent = s.muted || (s.music === 0 && s.sfx === 0);
  const Icon = silent ? VolumeX : s.music < 0.4 ? Volume1 : Volume2;

  return (
    <div ref={ref} className={`relative shrink-0 ${className}`}>
      <button
        onClick={() => setOpen((o) => !o)}
        className={`rounded-lg transition-colors ${
          labeled
            ? 'flex items-center gap-2 px-3 py-2 border border-ink-700 bg-ink-850 text-sm font-semibold hover:border-ink-500'
            : 'p-2 hover:bg-ink-800'
        } ${silent ? 'text-ink-500 hover:text-ink-200' : 'text-ink-300 hover:text-ink-50'}`}
        aria-label="Sonido"
        aria-expanded={open}
        title="Sonido"
      >
        <Icon className="w-5 h-5" />
        {labeled && <span>{silent ? 'Silenciado' : 'Sonido'}</span>}
      </button>

      {open && (
        <div
          role="dialog"
          aria-label="Sonido"
          className={`absolute z-50 top-full mt-2 ${align === 'right' ? 'right-0' : 'left-0'} w-64 surface-raised p-4 space-y-4 shadow-xl animate-pop`}
        >
          <button
            onClick={() => setAudioSettings({ muted: !s.muted })}
            aria-pressed={s.muted}
            className={`w-full flex items-center justify-center gap-2 py-2 rounded-lg text-sm font-semibold border transition-colors ${
              s.muted
                ? 'border-ember-500 bg-ember-500/15 text-ember-300'
                : 'border-ink-600 bg-ink-850 text-ink-200 hover:border-ink-500'
            }`}
          >
            {s.muted ? <VolumeX className="w-4 h-4" /> : <Volume2 className="w-4 h-4" />}
            {s.muted ? 'Activar sonido' : 'Silenciar todo'}
          </button>

          <Slider
            icon={<Music className="w-4 h-4" />}
            label="Música"
            value={s.music}
            disabled={s.muted}
            onChange={(music) => setAudioSettings({ music })}
          />
          <Slider
            icon={<Sparkles className="w-4 h-4" />}
            label="Efectos"
            value={s.sfx}
            disabled={s.muted}
            onChange={(sfx) => setAudioSettings({ sfx })}
            onCommit={playSelectSound}
          />
          <p className="text-ink-500 text-[11px] leading-snug">Solo cambia el sonido en tu dispositivo.</p>
        </div>
      )}
    </div>
  );
};

const Slider: React.FC<{
  icon: React.ReactNode;
  label: string;
  value: number;
  disabled?: boolean;
  onChange: (v: number) => void;
  onCommit?: () => void;
}> = ({ icon, label, value, disabled, onChange, onCommit }) => (
  <label className={`block space-y-1.5 ${disabled ? 'opacity-50' : ''}`}>
    <span className="flex items-center gap-2 text-sm text-ink-200">
      <span className="text-ink-400">{icon}</span>
      {label}
      <span className="ml-auto font-mono text-xs text-ink-500 tabular-nums">{Math.round(value * 100)}%</span>
    </span>
    <input
      type="range"
      min={0}
      max={100}
      step={5}
      value={Math.round(value * 100)}
      disabled={disabled}
      onChange={(e) => onChange(Number(e.target.value) / 100)}
      onPointerUp={onCommit}
      onKeyUp={onCommit}
      className="w-full accent-ember-500 cursor-pointer disabled:cursor-not-allowed"
    />
  </label>
);
